import { useEffect, useRef, useState, useCallback } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Play, X, ChevronLeft, ChevronRight } from "lucide-react";
import { mediaUrl } from "@/lib/api";

const src = (item) => (item.external ? item.url : mediaUrl(item.url));

const VideoItem = ({ item }) => {
  const ref = useRef(null);
  return (
    <div
      className="relative overflow-hidden group"
      onMouseEnter={() => ref.current?.play().catch(() => {})}
      onMouseLeave={() => ref.current?.pause()}
    >
      <video
        ref={ref}
        src={src(item)}
        muted
        loop
        playsInline
        preload="metadata"
        className="w-full grayscale-[0.8] transition-[filter,transform] duration-700 group-hover:grayscale-0 group-hover:scale-105"
      />
      <div className="absolute inset-0 flex items-center justify-center pointer-events-none transition-opacity duration-500 group-hover:opacity-0">
        <span className="w-14 h-14 rounded-full bg-black/60 backdrop-blur border border-gold/50 flex items-center justify-center">
          <Play size={18} className="text-gold ml-0.5" />
        </span>
      </div>
      <span className="absolute top-3 left-3 text-[10px] font-mono uppercase tracking-[0.2em] bg-black/70 text-gold px-2.5 py-1">
        Vidéo
      </span>
    </div>
  );
};

const Lightbox = ({ items, index, onClose, onNav }) => {
  const item = items[index];
  const onKey = useCallback(
    (e) => {
      if (e.key === "Escape") onClose();
      if (e.key === "ArrowRight") onNav(1);
      if (e.key === "ArrowLeft") onNav(-1);
    },
    [onClose, onNav]
  );

  useEffect(() => {
    window.addEventListener("keydown", onKey);
    document.body.style.overflow = "hidden";
    return () => {
      window.removeEventListener("keydown", onKey);
      document.body.style.overflow = "";
    };
  }, [onKey]);

  if (!item) return null;

  return (
    <motion.div
      data-testid="gallery-lightbox"
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      transition={{ duration: 0.35 }}
      onClick={onClose}
      className="fixed inset-0 z-[90] bg-black/92 backdrop-blur-xl flex items-center justify-center p-6 lg:p-16"
    >
      <button onClick={onClose} data-testid="lightbox-close" aria-label="Fermer"
        className="absolute top-6 right-6 w-12 h-12 border border-white/20 text-white/70 flex items-center justify-center transition-colors duration-300 hover:border-gold hover:text-gold z-10">
        <X size={20} />
      </button>
      {items.length > 1 && (
        <>
          <button onClick={(e) => { e.stopPropagation(); onNav(-1); }} data-testid="lightbox-prev" aria-label="Précédent"
            className="absolute left-4 lg:left-10 top-1/2 -translate-y-1/2 w-12 h-12 border border-white/20 text-white/70 flex items-center justify-center transition-colors duration-300 hover:border-gold hover:text-gold z-10">
            <ChevronLeft size={22} />
          </button>
          <button onClick={(e) => { e.stopPropagation(); onNav(1); }} data-testid="lightbox-next" aria-label="Suivant"
            className="absolute right-4 lg:right-10 top-1/2 -translate-y-1/2 w-12 h-12 border border-white/20 text-white/70 flex items-center justify-center transition-colors duration-300 hover:border-gold hover:text-gold z-10">
            <ChevronRight size={22} />
          </button>
        </>
      )}
      <motion.div
        key={item.id}
        initial={{ opacity: 0, scale: 0.96 }}
        animate={{ opacity: 1, scale: 1 }}
        transition={{ duration: 0.4, ease: [0.16, 1, 0.3, 1] }}
        onClick={(e) => e.stopPropagation()}
        className="max-w-full max-h-full flex flex-col items-center"
      >
        {item.type === "video" ? (
          <video src={src(item)} controls autoPlay playsInline
            className="max-w-full max-h-[80vh] border border-gold/30" data-testid="lightbox-video" />
        ) : (
          <img src={src(item)} alt={item.title || "Œuvre"}
            className="max-w-full max-h-[80vh] object-contain border border-gold/30" data-testid="lightbox-image" />
        )}
        {item.title && (
          <p className="mt-5 text-xs font-mono uppercase tracking-[0.25em] text-gold/80">{item.title}</p>
        )}
      </motion.div>
    </motion.div>
  );
};

export const GalleryGrid = ({ items }) => {
  const [selected, setSelected] = useState(null);
  const nav = (dir) => setSelected((i) => (i + dir + items.length) % items.length);

  return (
    <>
      <div className="columns-1 sm:columns-2 lg:columns-3 gap-5" data-testid="gallery-grid">
        {items.map((item, i) => (
          <motion.figure
            key={item.id}
            initial={{ y: 40, opacity: 0 }}
            whileInView={{ y: 0, opacity: 1 }}
            viewport={{ once: true, margin: "-40px" }}
            transition={{ duration: 0.8, delay: (i % 3) * 0.08, ease: [0.16, 1, 0.3, 1] }}
            className="break-inside-avoid mb-5 border border-transparent transition-colors duration-500 hover:border-gold/50 cursor-pointer"
            data-testid={`gallery-item-${item.id}`}
            onClick={() => setSelected(i)}
          >
            {item.type === "video" ? (
              <VideoItem item={item} />
            ) : (
              <div className="overflow-hidden group">
                <img
                  src={src(item)}
                  alt={item.title || "Œuvre New Creation Studio"}
                  loading="lazy"
                  className="w-full grayscale-[0.8] transition-[filter,transform] duration-700 group-hover:grayscale-0 group-hover:scale-105"
                />
              </div>
            )}
            {item.title && (
              <figcaption className="py-3 text-xs font-mono uppercase tracking-[0.2em] text-white/40">
                {item.title}
              </figcaption>
            )}
          </motion.figure>
        ))}
      </div>
      <AnimatePresence>
        {selected !== null && (
          <Lightbox items={items} index={selected} onClose={() => setSelected(null)} onNav={nav} />
        )}
      </AnimatePresence>
    </>
  );
};
